/** Разбор ответов `application/problem+json` (MVP_TECH_RFC §21). */

export interface FieldError {
  field: string;
  code: string;
  message: string;
}

export interface ProblemDetail {
  type: string;
  title: string;
  status: number;
  code: string;
  detail?: string | null;
  instance?: string | null;
  request_id?: string | null;
  field_errors: FieldError[];
  /**
   * Extension member RFC 7807: машиночитаемый контекст отказа, когда одного
   * кода экрану мало. Пример — `SEARCH_LIMIT_REACHED` со списком кандидатов на
   * архивирование (MVP_PRD §20). Текстов здесь нет: переводит фронт.
   */
  context?: Record<string, unknown> | null;
}

export class ApiError extends Error {
  readonly problem: ProblemDetail;
  /**
   * Значение заголовка `Retry-After` в секундах, если сервер его прислал.
   * Приходит с 429: по нему интерфейс рисует таймер повторной отправки.
   */
  readonly retryAfterSeconds: number | null;

  constructor(problem: ProblemDetail, retryAfterSeconds: number | null = null) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  get status(): number {
    return this.problem.status;
  }

  /** Машинный код — единственное, на что можно завязывать логику UI. */
  get code(): string {
    return this.problem.code;
  }

  get requestId(): string | null {
    return this.problem.request_id ?? null;
  }

  get fieldErrors(): FieldError[] {
    return this.problem.field_errors;
  }

  /** Контекст отказа. Форму знает вызывающий код: здесь она не угадывается. */
  get context(): Record<string, unknown> | null {
    return this.problem.context ?? null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** `Retry-After` в секундах. Формат с датой RFC 7231 backend не использует. */
export function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

export function toProblemDetail(payload: unknown, fallbackStatus: number): ProblemDetail {
  if (isRecord(payload) && typeof payload['code'] === 'string') {
    return {
      type: typeof payload['type'] === 'string' ? payload['type'] : 'about:blank',
      title: typeof payload['title'] === 'string' ? payload['title'] : 'Error',
      status: typeof payload['status'] === 'number' ? payload['status'] : fallbackStatus,
      code: payload['code'],
      detail: typeof payload['detail'] === 'string' ? payload['detail'] : null,
      instance: typeof payload['instance'] === 'string' ? payload['instance'] : null,
      request_id: typeof payload['request_id'] === 'string' ? payload['request_id'] : null,
      field_errors: Array.isArray(payload['field_errors'])
        ? (payload['field_errors'] as FieldError[])
        : [],
      context: isRecord(payload['context']) ? payload['context'] : null,
    };
  }

  return {
    type: 'about:blank',
    title: 'Unexpected error',
    status: fallbackStatus,
    code: fallbackStatus === 0 ? 'NETWORK_ERROR' : 'UNEXPECTED_ERROR',
    detail: null,
    instance: null,
    request_id: null,
    field_errors: [],
    context: null,
  };
}
